import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { IRowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep, debounce } from 'lodash';
import { Toolbar } from 'primereact/toolbar';
import { useDispatch } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  useGetCsrDay7Query,
  useUpdateFreshTransfersMutation,
} from '@/app/services/excel';
import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { TOG_CsrDay7 } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import { useContextMenu } from '@/utils/context-menu';

import { csrDay7ColDefs, excelStyles } from './columnDefs';
import { CsrDay7Actions } from './CsrDay7Actions';
import { CsrDay7Selector } from './CsrDay7Selector';

import './CsrDay7.css';

const DEBOUNCE_DELAY_MS = 600;
export const CsrDay7Page = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const gridRef = useRef<AgGridReact>(null);

  const location = useLocation();
  const [params] = useSearchParams();
  const beginDate = params.get('beginDate');
  const endDate = params.get('endDate');
  const siteId = params.get('site');
  const accountId = params.get('account');

  const [isUpdateRunning, setIsUpdateRunning] = useState(false);
  const [totals, setTotals] = useState({
    frozen: 0,
    implanted: 0,
    discarded: 0,
    shipped: 0,
    d65shipped: 0,
    d65Frozen: 0,
    d65Implanted: 0,
  });

  const rowDataRef = useRef<TOG_CsrDay7[]>([]);

  const [updateFreshTransfers] = useUpdateFreshTransfersMutation();

  const { data: csrDay7Data, refetch } = useGetCsrDay7Query(
    {
      beginDate,
      endDate,
      siteId,
      accountId,
    },
    { skip: !beginDate || !endDate },
  );

  const retryAsync = async <T,>(
    fn: () => Promise<T>,
    shouldRetry: (result: T) => boolean,
    maxAttempts: number = 2,
  ): Promise<T> => {
    let lastResult: T;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        lastResult = await fn();
        if (!shouldRetry(lastResult)) {
          return lastResult;
        }
      } catch (error) {
        if (attempt === maxAttempts) {
          throw error;
        }
      }
    }
    return lastResult!;
  };

  const handleUpdateSingleFreshTransfer = useCallback(
    async (data: TOG_CsrDay7) => {
      if (isUpdateRunning) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'Operation Blocked',
            detail: 'Please wait for the current operation to complete',
            life: 3000,
          }),
        );
        return;
      }

      setIsUpdateRunning(true);
      try {
        const response = await retryAsync(
          async () => {
            const res = await updateFreshTransfers({
              embryoUpdates: [
                {
                  embryoId: data?.embryoId,
                  embryoToBe: data?.embryoToBe,
                  implantType: data?.implantType,
                  fertilizationId: data?.fertilizationId,
                },
              ],
            }).unwrap();
            return res;
          },
          (result) => result?.updateFreshTransfers?.code === 500,
          3,
        );

        switch (response?.updateFreshTransfers?.code) {
          case 500:
            dispatch(
              addToast({
                severity: 'error',
                summary: 'Server Error',
                detail: response?.updateFreshTransfers?.message,
                sticky: true,
              }),
            );
            refetch();
            break;

          case 200:
            dispatch(
              addToast({
                severity: 'success',
                summary: 'Success',
                detail: response?.updateFreshTransfers?.message,
                life: 3000,
              }),
            );
            break;
        }
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update embryo. Please try again.',
            sticky: true,
          }),
        );
      } finally {
        setIsUpdateRunning(false);
      }
    },
    [dispatch, refetch, isUpdateRunning],
  );
  const debouncedUpdateRef = useRef(
    debounce(handleUpdateSingleFreshTransfer, DEBOUNCE_DELAY_MS),
  );

  const handleUpdateBatchFreshTransfers = async (
    nodes: IRowNode[],
    embryoStatus: string,
  ): Promise<void> => {
    const embryoUpdates = nodes.map((node) => ({
      embryoId: node.data?.embryoId,
      embryoToBe: embryoStatus,
      implantType: node.data?.implantType,
      fertilizationId: node.data?.fertilizationId,
    }));

    try {
      const response = await retryAsync(
        async () => {
          const res = await updateFreshTransfers({ embryoUpdates }).unwrap();
          return res;
        },
        (result) => result?.updateFreshTransfers?.code === 500,
        3,
      );

      switch (response?.updateFreshTransfers?.code) {
        case 500:
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Server Error',
              detail: response?.updateFreshTransfers?.message,
              sticky: true,
            }),
          );
          refetch();
          break;

        case 200:
          dispatch(
            addToast({
              severity: 'success',
              summary: 'Success',
              detail: response?.updateFreshTransfers?.message,
              life: 3000,
            }),
          );
          refetch();
          break;
      }
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Network Error',
          detail: 'Failed to update embryos after retrying.',
          sticky: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (csrDay7Data) {
      const newTotals = calculateTotals(csrDay7Data);
      if (newTotals) {
        setTotals(newTotals);
      }
      rowDataRef.current = cloneDeep(csrDay7Data);
    }
  }, [csrDay7Data]);

  useEffect(() => {
    if (beginDate && endDate && siteId) {
      refetch();
    }
  }, [refetch, location.pathname, beginDate, endDate, siteId]);

  const calculateTotals = (data: TOG_CsrDay7[]) => {
    const totals = {
      frozen: 0,
      discarded: 0,
      implanted: 0,
      shipped: 0,
      d65shipped: 0,
      d65Frozen: 0,
      d65Implanted: 0,
    };

    data.forEach((item) => {
      switch (item.embryoToBe) {
        case 'Implanted':
          totals.implanted += 1;
          break;
        case 'Frozen':
          totals.frozen += 1;
          break;
        case 'Discarded':
          totals.discarded += 1;
          break;
        case 'D6.5-Shipped':
          totals.d65shipped += 1;
          break;
        case 'D6.5-Frozen':
          totals.d65Frozen += 1;
          break;
        case 'D6.5-Implanted':
          totals.d65Implanted += 1;
          break;
      }
    });

    return totals;
  };

  const handleFrozen = () => {
    const displayedNodes = gridRef.current?.api.getRenderedNodes();
    if (!displayedNodes?.length) return;

    const selectedNodes = displayedNodes.filter((node) => node.isSelected());
    if (!selectedNodes.length) return;

    handleUpdateBatchFreshTransfers(selectedNodes, 'Frozen');
  };

  const handleImplanted = () => {
    const displayedNodes = gridRef.current?.api.getRenderedNodes();
    if (!displayedNodes?.length) return;

    const selectedNodes = displayedNodes.filter((node) => node.isSelected());
    if (!selectedNodes.length) return;

    handleUpdateBatchFreshTransfers(selectedNodes, 'Implanted');
  };

  const handleDiscarded = () => {
    const displayedNodes = gridRef.current?.api.getRenderedNodes();
    if (!displayedNodes?.length) return;

    const selectedNodes = displayedNodes.filter((node) => node.isSelected());
    if (!selectedNodes.length) return;

    handleUpdateBatchFreshTransfers(selectedNodes, 'Discarded');
  };

  const { getContextMenuItems } = useContextMenu({
    idKey: 'embryoId',
  });

  return (
    <div className="common-page csrDay7">
      <Toolbar
        className="toolbar-above-panel"
        start={<CsrDay7Selector totals={totals} />}
        end={
          <CsrDay7Actions
            csrDay7Data={rowDataRef.current as TOG_CsrDay7[]}
            gridRef={gridRef}
            handleFrozen={handleFrozen}
            handleImplanted={handleImplanted}
            handleDiscarded={handleDiscarded}
            refetch={refetch}
            disabled={isUpdateRunning}
          />
        }
      />

      <div className="csrDay7__grid">
        <WithLocalStorage storageKey="csrDay7-grid">
          <AgGridReact
            gridOptions={defaultGridOptions}
            getRowId={(params) => params.data.embryoId}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
            defaultColDef={defaultColDef}
            columnDefs={csrDay7ColDefs}
            getContextMenuItems={getContextMenuItems}
            excelStyles={excelStyles}
            rowData={rowDataRef.current}
            ref={gridRef}
            animateRows={false}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            context={{ isUpdateRunning }}
            onCellValueChanged={(params) => {
              if (isUpdateRunning) {
                params.api.stopEditing();
                return;
              }

              const rowIndex = rowDataRef.current.findIndex(
                (row) => row === params.data,
              );

              debouncedUpdateRef.current(params.data);
              if (rowIndex !== -1) {
                rowDataRef.current[rowIndex] = {
                  ...rowDataRef.current[rowIndex],
                  [params.column.getId()]: params.newValue,
                };
              }

              const newTotals = calculateTotals(rowDataRef.current);
              setTotals(newTotals);
            }}
            sideBar={sideBar}
          />
        </WithLocalStorage>
      </div>
    </div>
  );
};
